import { useEffect, useRef } from 'react';
import * as Sentry from '@sentry/react-native';
import { usePathname } from 'expo-router';
import { useAuth } from '@/components/providers/AuthProvider';
import { ApiError } from '@/services/ApiService';
import { addReactRootDiagnostics } from '@/services/ReactRootDiagnostics';

/**
 * Expected server-driven ApiError responses that are not app bugs and should
 * not reach Sentry:
 * - 401 "Authorization has been denied" — returned on every request for banned
 *   accounts and expired sessions
 * - "Your account is under temporary ban" — the ban payload itself
 * - 502 with the axios fallback message — gateway/proxy failures during deploys
 *   and outages, nothing actionable in app code
 */
const isExpectedApiError = (error: unknown): boolean => {
  if (!(error instanceof ApiError)) return false;
  return (
    (error.status === 401 &&
      error.message.startsWith('Authorization has been denied for this request')) ||
    (error.status === 502 && error.message.startsWith('Request failed with status code 502')) ||
    error.message.startsWith('Your account is under temporary ban')
  );
};

/**
 * AsyncStorage manifest writes fail when the device has no free disk space
 * (NSCocoaErrorDomain 640 / NSPOSIXErrorDomain 28 "No space left on device").
 * Not fixable from app code, so keep these out of Sentry.
 */
const isDiskFullStorageError = (error: unknown): boolean =>
  error instanceof Error &&
  error.message.includes('Failed to write manifest file') &&
  (error.message.includes('No space left on device') || error.message.includes('out of space'));

// Stack-frame markers of UIKit's keyboard input-mode controller. App Hangs
// rooted in these frames are an Apple bug in iOS 17+ (UIKeyboardInputModeController
// / globeKeyDisplaysAsEmojiKey stalls when a TextInput becomes first responder)
// and are not actionable from app code.
const UIKIT_KEYBOARD_HANG_FRAMES = [
  'UIKeyboardInputModeController',
  'globeKeyDisplaysAsEmojiKey',
  'setInputViewSet',
  'UIKeyboardSceneDelegate',
  'UIInputWindowController',
];

const isUIKitKeyboardHang = (event: Sentry.ErrorEvent): boolean => {
  const exceptionValue = event.exception?.values?.[0];
  if (exceptionValue?.mechanism?.type !== 'AppHang') return false;
  const frames = exceptionValue.stacktrace?.frames;
  if (!Array.isArray(frames)) return false;
  return frames.some((frame) => {
    const fn = frame?.function;
    if (typeof fn !== 'string') return false;
    return UIKIT_KEYBOARD_HANG_FRAMES.some((marker) => fn.includes(marker));
  });
};

/**
 * Sentry beforeSend hook: drops noise events that are not actionable app bugs
 * (expected ApiErrors, disk-full AsyncStorage writes, UIKit keyboard App Hangs)
 * and attaches React root diagnostics to "Maximum update depth" errors on web.
 * Fail-open: if anything throws while inspecting the event, the event is sent
 * unchanged so we never accidentally drop a legitimate report.
 */
export const sentryBeforeSend: NonNullable<Sentry.ReactNativeOptions['beforeSend']> = (
  event,
  hint
) => {
  try {
    const originalException = hint?.originalException;
    if (
      isExpectedApiError(originalException) ||
      isDiskFullStorageError(originalException) ||
      isUIKitKeyboardHang(event)
    ) {
      return null;
    }
    addReactRootDiagnostics(event);
    return event;
  } catch {
    return event;
  }
};

/**
 * Hook that observes auth state and updates Sentry user context.
 * Should be used once in the app root, inside AuthProvider.
 */
export const useSentryUserContext = (): void => {
  const { accountStatus, currentCommunity } = useAuth();
  const prevMemberIdRef = useRef<number | undefined>(undefined);
  const prevCommunityRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const memberId = accountStatus?.MemberId;
    const username = accountStatus?.UserName;

    // Only update if values actually changed
    if (memberId === prevMemberIdRef.current && currentCommunity === prevCommunityRef.current) {
      return;
    }

    prevMemberIdRef.current = memberId;
    prevCommunityRef.current = currentCommunity;

    if (memberId && username && currentCommunity) {
      Sentry.setUser({
        id: `${currentCommunity}:${memberId}`,
        username: username,
      });
      Sentry.setTag('community', currentCommunity);
    } else {
      Sentry.setUser(null);
      Sentry.setTag('community', 'none');
    }
  }, [accountStatus?.MemberId, accountStatus?.UserName, currentCommunity]);
};

/**
 * Hook that tags the active route on Sentry and leaves a navigation breadcrumb.
 * This attributes screen-less events (ANRs, App Hangs, native crashes) to the
 * screen the user was on, which the SDK does not capture on its own.
 * Should be used once in the app root, inside the navigation tree.
 */
export const useSentryRouteContext = (): void => {
  const pathname = usePathname();

  useEffect(() => {
    if (!pathname) return;

    Sentry.setTag('route', pathname);
    Sentry.addBreadcrumb({
      category: 'navigation',
      message: pathname,
      level: 'info',
    });
  }, [pathname]);
};
